import store from "@/store";

function stationIdInList(stationId, list) {
	return (list || []).some((s) => String(s) === String(stationId));
}

export class ProductOrderService {
	constructor() {}

	/**
	 * Indica si el ítem de pedido se trata como "ghost" (fuera del foco de estaciones) para la cocina.
	 * - Lista de exclusión: ghost si el producto intersecta excludedStations (con ids de partida).
	 * - Solo exclusión en partidas reales, sin «Ver sin partidas»: sin ids de partida en el producto →
	 *   ghost si showProductOrdersWithoutElaborationWhenExcludingOnly es false.
	 * - Sin ninguna partida en Ver/Ocultar y «Ocultar» en Sin partidas: ocultar solo ítems sin partida (sin
	 *   necesidad de haber marcado Ocultar en una partida concreta).
	 * - «Solo» en partidas (selectedStations) y/o «Solo sin partidas» (onlyProductOrdersWithoutElaborationStation):
	 *   visible si cumple cualquiera de las dos (unión): sin partida con id, o parte de la lista incluida.
	 */
	checkIsGhostProductOrder(productOrder) {
		const stations = productOrder?.elaborationStations;
		const productElaborationStationIds =
			Array.isArray(stations) && stations.length > 0
				? stations.map((es) => es?.id).filter((id) => id != null)
				: [];
		return this.checkIsGhostByStationIds(productElaborationStationIds);
	}

	/**
	 * «Todas las partidas» sin ningún Ver/Ocultar/Sin-partida activo: no hay filtro real aplicado.
	 * Si el estado quedó incoherente (showAll true pero hay exclusiones / Ver / etc.), no cuenta
	 * como "sin filtro" y seguimos evaluando normalmente.
	 */
	hasNoActiveStationFilter() {
		const showAll = store.getters["copilot/showAllProductOrdersInKitchen"];
		const onlyNoStation = store.getters["copilot/onlyProductOrdersWithoutElaborationStation"];
		const includeIds = store.getters["copilot/selectedStations"] || [];
		const excludeIds = store.getters["copilot/excludedStations"] || [];
		const showWithoutWhenExcludingOnly = store.getters["copilot/showProductOrdersWithoutElaborationWhenExcludingOnly"];

		const hideSinPartidaSinOtrasPartidas =
			!onlyNoStation &&
			includeIds.length === 0 &&
			excludeIds.length === 0 &&
			showWithoutWhenExcludingOnly === false;

		const hasActiveStationFilter =
			excludeIds.length > 0 ||
			includeIds.length > 0 ||
			onlyNoStation ||
			hideSinPartidaSinOtrasPartidas;

		return showAll && !hasActiveStationFilter;
	}

	/**
	 * Misma lógica de partidas que checkIsGhostProductOrder, pero sobre una lista plana de ids de
	 * partida (como la que llega en notification.productOrders[].elaborationStations desde backend).
	 */
	checkIsGhostByStationIds(productElaborationStationIds) {
		if (this.hasNoActiveStationFilter()) {
			return false;
		}

		const onlyNoStation = store.getters["copilot/onlyProductOrdersWithoutElaborationStation"];
		const includeIds = store.getters["copilot/selectedStations"] || [];
		const excludeIds = store.getters["copilot/excludedStations"] || [];
		const showWithoutWhenExcludingOnly = store.getters["copilot/showProductOrdersWithoutElaborationWhenExcludingOnly"];

		const hideSinPartidaSinOtrasPartidas =
			!onlyNoStation &&
			includeIds.length === 0 &&
			excludeIds.length === 0 &&
			showWithoutWhenExcludingOnly === false;

		const stationIds = Array.isArray(productElaborationStationIds)
			? productElaborationStationIds.filter((id) => id != null)
			: [];
		const hasStationIds = stationIds.length > 0;

		if (excludeIds.length > 0 && hasStationIds) {
			if (stationIds.some((id) => stationIdInList(id, excludeIds))) {
				return true;
			}
		}

		if (excludeIds.length > 0 && includeIds.length === 0 && !onlyNoStation) {
			if (!hasStationIds) return !showWithoutWhenExcludingOnly;
		}

		if (hideSinPartidaSinOtrasPartidas) {
			if (!hasStationIds) return true;
			return false;
		}

		if (onlyNoStation || includeIds.length > 0) {
			const matchesNoStationLeg = onlyNoStation && !hasStationIds;
			const matchesStationLeg =
				includeIds.length > 0 &&
				hasStationIds &&
				stationIds.some((id) => stationIdInList(id, includeIds));
			const visible = matchesNoStationLeg || matchesStationLeg;
			return !visible;
		}

		return false;
	}

	/**
	 * Decide si una notificación es relevante bajo el filtro de partidas actual.
	 * Las notificaciones de pedidos llegan con productOrders: [{ id, productId, elaborationStations: [ids] }].
	 * Es relevante (se debe reproducir) si al menos una de sus líneas es visible con el filtro
	 * de partidas activo. Sin datos de productOrders (notificaciones antiguas o generales) → relevante.
	 */
	isNotificationRelevantForStationFilter(notification) {
		const productOrders = notification?.productOrders;
		if (!Array.isArray(productOrders) || productOrders.length === 0) {
			return true;
		}
		return productOrders.some((productOrder) => !this.checkIsGhostByStationIds(productOrder?.elaborationStations));
	}
}

export const productOrderService = new ProductOrderService();
